import { t } from '@trimble/i18n';
import type { LengthUnit } from '@fulcrum/lib';
import { useQueryClient } from '@tanstack/solid-query';
import {
  Component,
  createMemo,
  createSignal,
  onMount,
  Show,
  untrack,
} from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { HeatmapPaletteApiService } from '../heatmap-palette-api-service';
import {
  colorAtValue,
  formatStopValueInMeters,
  parseDisplayStopValueToMeters,
  parseStopValue,
  sortStopsByValueDescending,
} from '../heatmap-stop-layout';

import {
  EditablePalette,
  EditableStop,
  TextureType,
} from '../types/heatmap.types';
import { useVolumeHeatmap } from '../VolumeHeatmapProvider';
import { NameInput } from './components/NameInput';
import { NewColorStop } from './components/NewColorStop';
import { PaletteStopViewer } from './components/PaletteStopViewer';
import { createPaletteStopColorPickerGroup } from './components/createPaletteStopColorPickerControls';
import { TextureSelection } from './components/TextureSelection';
import { HeatmapSectionLabel } from '../components/HeatmapSectionLabel';
import { PanelLayout } from '@tcweb/connect-ui';

export interface EditPalettePanelProps {
  /** Project length unit used for the boundary value labels. Defaults to 'm'. */
  readonly lengthUnit?: LengthUnit;
  onBack(): void;
  onClose(): void;
}

const NEW_PALETTE_DEFAULTS: EditablePalette = {
  id: '',
  name: 'measurements.volumetric.gradient.newPaletteName',
  textureType: 'gradient',
  default: false,
  colorAboveRange: '#6A6E79',
  colorBelowRange: '#6A6E79',
  stops: [
    { id: 'cut', color: '#D50000', value: '0.200' },
    { id: 'net', color: '#8BC34A', value: '0' },
    { id: 'fill', color: '#01579B', value: '-0.200' },
  ],
};

/** The measurements API requires a palette to keep at least two stops. */
const MIN_STOP_COUNT = 2;

export const EditPalettePanel: Component<EditPalettePanelProps> = (props) => {
  const volumeHeatmap = useVolumeHeatmap();
  const palette =
    untrack(() => volumeHeatmap.selectedPalette()) ?? NEW_PALETTE_DEFAULTS;
  const isNewPalette = palette.id === '';

  const [name, setName] = createSignal(t(palette.name));

  const queryClient = useQueryClient();
  const apiService = new HeatmapPaletteApiService(queryClient);
  const setDefaultMutation = apiService.setDefaultPaletteMutation();
  const deleteMutation = apiService.deletePaletteMutation();

  onMount(() => {
    resetMutationErrors();
  });

  function resetMutationErrors(): void {
    setDefaultMutation.reset();
    deleteMutation.reset();
  }

  const setAsDefaultError = createMemo(() =>
    setDefaultMutation.isError
      ? t('measurements.volumetric.gradient.setAsDefaultError')
      : undefined
  );
  const deleteError = createMemo(() =>
    deleteMutation.isError
      ? t('measurements.volumetric.gradient.deleteError')
      : undefined
  );

  const isDefault = createMemo(
    () => volumeHeatmap.selectedPalette()?.default ?? palette.default
  );

  const showDefaultChip = createMemo(() => isDefault());

  const showSetAsDefaultButton = createMemo(() => !isDefault());

  const [textureType, setTextureType] = createSignal<TextureType>(
    palette.textureType
  );

  const [colorAboveRange, setColorAboveRange] = createSignal(
    palette.colorAboveRange
  );
  const [colorBelowRange, setColorBelowRange] = createSignal(
    palette.colorBelowRange
  );

  const lengthUnit = (): LengthUnit => props.lengthUnit ?? 'm';

  const [stops, setStops] = createStore<EditableStop[]>(
    palette.stops.map((stop) => ({ ...stop }))
  );

  const existingValuesInMeters = createMemo(() =>
    stops.map((stop) => parseStopValue(stop.value))
  );

  // Shared across the new-stop input and every stop row so that only one colour
  // picker is open at a time; opening another cancels the currently open one.
  const colorPickerGroup = createPaletteStopColorPickerGroup();

  const previewPalette = createMemo<EditablePalette>(() => ({
    ...palette,
    stops,
    textureType: textureType(),
    colorAboveRange: colorAboveRange(),
    colorBelowRange: colorBelowRange(),
  }));

  function handleAddStop(valueInMeters: number, color?: string): void {
    const newStop: EditableStop = {
      id: crypto.randomUUID(),
      color: color ?? colorAtValue(valueInMeters, stops),
      value: formatStopValueInMeters(valueInMeters, lengthUnit()),
    };
    setStops(
      reconcile(sortStopsByValueDescending([...stops, newStop]), { key: 'id' })
    );
  }

  function parseNewStopToMeters(display: string): number | null {
    return parseDisplayStopValueToMeters(display, lengthUnit());
  }

  function handleStopValueCommit(id: string, displayValue: string): void {
    const meters = parseDisplayStopValueToMeters(displayValue, lengthUnit());
    if (meters === null) return;

    if (!stops.some((stop) => stop.id === id)) return;

    const updated = stops.map((stop) =>
      stop.id === id
        ? { ...stop, value: formatStopValueInMeters(meters, lengthUnit()) }
        : { ...stop }
    );
    setStops(reconcile(sortStopsByValueDescending(updated), { key: 'id' }));
  }

  function handleRemoveStop(id: string): void {
    if (stops.length <= MIN_STOP_COUNT) return;

    setStops(
      reconcile(
        stops.filter((stop) => stop.id !== id),
        { key: 'id' }
      )
    );
  }

  function handleStopColorChange(id: string, color: string): void {
    const index = stops.findIndex((stop) => stop.id === id);
    if (index === -1) return;

    setStops(index, 'color', color);
  }

  function handleAboveColorChange(color: string): void {
    setColorAboveRange(color);
  }

  function handleBelowColorChange(color: string): void {
    setColorBelowRange(color);
  }

  function handleBack(): void {
    volumeHeatmap.cancelPaletteEdit();
    props.onBack();
  }

  function handleClose(): void {
    volumeHeatmap.cancelPaletteEdit();
    props.onClose();
  }

  async function handleDelete(): Promise<void> {
    resetMutationErrors();
    deleteMutation.mutate(
      {
        paletteId: palette.id,
        tenantTrn: volumeHeatmap.tenantTrn(),
      },
      {
        onSuccess: () => {
          if (volumeHeatmap.selectedPalette()?.id === palette.id) {
            volumeHeatmap.selectDefaultPalette();
          }
          props.onBack();
        },
      }
    );
  }

  function handleSave(): void {
    const edited: EditablePalette = {
      ...palette,
      id: isNewPalette ? crypto.randomUUID() : palette.id,
      name: name(),
      textureType: textureType(),
      default: palette.default,
      colorAboveRange: colorAboveRange(),
      colorBelowRange: colorBelowRange(),
      stops: stops.map((stop) => ({ ...stop })),
    };
    volumeHeatmap.savePalette(edited);
    volumeHeatmap.completePaletteEdit();
    props.onBack();
  }

  async function handleSetAsDefault(): Promise<void> {
    resetMutationErrors();
    setDefaultMutation.mutate(
      {
        paletteId: palette.id,
        tenantTrn: volumeHeatmap.tenantTrn(),
      },
      {
        onSuccess: () => {
          const current = volumeHeatmap.selectedPalette();
          if (current?.id === palette.id) {
            volumeHeatmap.setSelectedPalette({ ...current, default: true });
          }
        },
      }
    );
  }

  return (
    <div class="flex flex-col h-full" data-testid="edit-palette-panel">
      <PanelLayout
        panelTitle={
          volumeHeatmap.editMode() == 'edit'
            ? t('measurements.volumetric.gradient.editPaletteTitle')
            : t('measurements.volumetric.gradient.addPaletteTitle')
        }
        leftActions={
          <modus-wc-tooltip
            content={t('general.back')}
            position="bottom"
            class="[&_div]:flex"
          >
            <modus-wc-button
              color="tertiary"
              shape="circle"
              size="md"
              type="button"
              variant="borderless"
              data-testid="edit-palette-back"
              on:buttonClick={handleBack}
            >
              <modus-wc-icon
                name="arrow_back"
                class="text-[var(--modus-wc-color-base-content,#171c1e)]"
              />
            </modus-wc-button>
          </modus-wc-tooltip>
        }
        rightActions={
          <>
            <Show when={!isNewPalette && !isDefault()}>
              <modus-wc-button
                color="tertiary"
                shape="circle"
                size="md"
                type="button"
                variant="borderless"
                data-testid="edit-palette-delete"
                onClick={() => void handleDelete()}
              >
                <modus-wc-icon name="delete" size="sm" />
              </modus-wc-button>
            </Show>
            <modus-wc-tooltip
              content={t('general.close')}
              position="bottom"
              class="[&_div]:flex"
            >
              <modus-wc-button
                color="tertiary"
                shape="circle"
                size="md"
                type="button"
                variant="borderless"
                data-testid="edit-palette-close"
                onClick={handleClose}
              >
                <modus-wc-icon
                  name="close"
                  size="md"
                  class="text-[var(--modus-wc-color-base-content,#171c1e)]"
                />
              </modus-wc-button>
            </modus-wc-tooltip>
          </>
        }
        content={
          <>
            <section class="flex flex-col h-full">
              <div class="flex flex-col grow gap-4 p-4 overflow-y-auto flex-1">
                <NameInput
                  name={name()}
                  onNameChange={setName}
                  showDefaultChip={showDefaultChip()}
                />

                <Show when={showSetAsDefaultButton()}>
                  <modus-wc-button
                    color="tertiary"
                    shape="rectangle"
                    size="md"
                    type="button"
                    variant="borderless"
                    attr:custom-class="!text-gray-6 hover:!text-gray-6"
                    data-testid="edit-palette-set-default"
                    onClick={() => {
                      void handleSetAsDefault();
                    }}
                  >
                    {t('measurements.volumetric.gradient.setAsDefault')}
                  </modus-wc-button>
                </Show>

                <Show when={setAsDefaultError()}>
                  <div data-testid="edit-palette-set-default-error">
                    <modus-wc-alert
                      attr:alert-description={setAsDefaultError() ?? ''}
                      role="alert"
                      variant="error"
                    />
                  </div>
                </Show>

                <Show when={deleteError()}>
                  <div data-testid="edit-palette-delete-error">
                    <modus-wc-alert
                      attr:alert-description={deleteError() ?? ''}
                      role="alert"
                      variant="error"
                    />
                  </div>
                </Show>

                <div
                  class="border-t border-t-gray-0"
                  data-testid="edit-palette-section-divider"
                  role="separator"
                />

                <section
                  class="flex flex-col gap-1"
                  data-testid="edit-palette-color-stops"
                >
                  <HeatmapSectionLabel
                    uppercase
                    label={t('measurements.volumetric.gradient.colors')}
                  />

                  <NewColorStop
                    lengthUnit={lengthUnit()}
                    existingValuesInMeters={existingValuesInMeters()}
                    parseToMeters={parseNewStopToMeters}
                    colorPickerGroup={colorPickerGroup}
                    onAddStop={handleAddStop}
                  />

                  <PaletteStopViewer
                    palette={previewPalette()}
                    lengthUnit={lengthUnit()}
                    editable
                    colorPickerGroup={colorPickerGroup}
                    onStopValueCommit={handleStopValueCommit}
                    onRemoveStop={handleRemoveStop}
                    onStopColorChange={handleStopColorChange}
                    onAboveColorChange={handleAboveColorChange}
                    onBelowColorChange={handleBelowColorChange}
                  />
                </section>

                <TextureSelection
                  setTextureType={setTextureType}
                  stops={stops}
                  textureType={textureType()}
                  colorAboveRange={colorAboveRange()}
                  colorBelowRange={colorBelowRange()}
                />
              </div>

              <div class="flex items-center justify-end gap-2 px-4 py-3 border-t border-t-gray-0  flex-shrink-0">
                <modus-wc-button
                  color="primary"
                  shape="rectangle"
                  size="md"
                  type="button"
                  variant="filled"
                  data-testid="edit-palette-save"
                  onClick={handleSave}
                >
                  {t('general.save')}
                </modus-wc-button>
              </div>
            </section>
          </>
        }
      />
    </div>
  );
};
